// src/components/MobileDrawer.jsx
import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '../store/useAppStore';
import { useServices } from '../hooks/useServices';
import CurrencySwitcher from './controls/CurrencySwitcher';
import LangSwitcher from './controls/LangSwitcher';
import RegionSelect from './RegionSelect';


/* ==== .env defaults ==== */
const PHONE_TEL = process.env.REACT_APP_PHONE_TEL || '+74959729797';
const PHONE_DISPLAY = process.env.REACT_APP_PHONE_DISPLAY || '+7 (495) 972-97-97';
const WHATSAPP_NUMBER = process.env.REACT_APP_WHATSAPP || '79309729797';
const TELEGRAM_URL = process.env.REACT_APP_TELEGRAM_URL || 'https://t.me/+79309729797';
const ZEN_URL = process.env.REACT_APP_ZEN_URL || 'https://zen.yandex.ru/astorius';
const VK_URL = process.env.REACT_APP_VK_URL || 'https://vk.com/astorius';
const OK_URL = process.env.REACT_APP_OK_URL || 'https://ok.ru/astorius';

/* ========= Styles ========= */
const Overlay = styled.div`
  position:fixed; inset:0; background:rgba(0,0,0,.35);
  opacity:${p=>p.open?1:0}; pointer-events:${p=>p.open?'auto':'none'};
  transition:opacity .18s ease; z-index:1098;
`;

const Drawer = styled.aside`
  position:fixed; top:0; left:0; height:100%; width:min(92vw, 360px);
  transform: translateX(${p=>p.open?'0':'-100%'});
  transition: transform .22s ease;
  /* Прозрачный стеклянный фон */
  background: ${({theme}) => theme.glass};
  -webkit-backdrop-filter: saturate(180%) blur(14px);
  backdrop-filter: saturate(180%) blur(14px);
  color: var(--fg);
  border-right:1px solid var(--bdr);
  box-shadow: 10px 0 24px rgba(0,0,0,.24);
  z-index:1099; display:flex; flex-direction:column;
`;

const Head = styled.div`
  display:flex; align-items:center; justify-content:space-between; gap:8px;
  padding:14px; border-bottom:1px solid var(--bdr);
  
  .logo {
    display: flex;
    align-items: center;
    
    img {
      height: 32px;
      display: block;
      object-fit: contain;
    }
  }
  
  strong{ font-weight:800; letter-spacing:.3px; }
  button{
    border:1px solid var(--bdr); border-radius:10px; padding:6px 10px; background:var(--card); color:var(--fg);
    box-shadow: var(--shadow); cursor:pointer;
  }
`;

const Body = styled.div`
  padding: 12px; 
  overflow-y: auto; 
  overflow-x: hidden;
  flex: 1; /* Занимает оставшееся пространство */
  min-height: 0; /* Позволяет flex-элементу сжиматься */
  
  /* Используем обычный поток документа вместо grid для лучшей работы с details */
  display: block;

  > * + * {
    margin-top: 10px; /* Отступы между группами */
  }

  a{ display:block; padding:10px 12px; border-radius:12px; }
  .group{ 
    border:1px solid var(--bdr); 
    border-radius:14px; 
    background:rgba(255,255,255,.04); 
    overflow: visible;
  }

  /* Аккордеоны */
  details{ 
    border-bottom:1px solid var(--bdr); 
    overflow: visible;
    /* Убираем любые ограничения высоты для полного раскрытия */
    max-height: none;
    height: auto;
  }
  details:last-of-type{ border-bottom:none; }
  details[open] summary{ background:rgba(0,0,0,.06); }
  
  /* Анимация открытия details */
  details[open] {
    animation: slideDown 0.3s ease-out;
  }
  
  @keyframes slideDown {
    from {
      max-height: 40px; /* Высота только summary */
    }
    to {
      max-height: 1000px; /* Увеличиваем для полного раскрытия всех элементов */
    }
  }
  
  summary{ 
    list-style:none; 
    cursor:pointer; 
    padding:12px; 
    font-weight:700; 
    position: relative;
    transition: background-color 0.2s ease;
  }
  summary::-webkit-details-marker{ display:none; }
  
  /* Стрелка для раскрывающихся списков */
  summary::after {
    content: '▼';
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%) rotate(0deg);
    transition: transform 0.2s ease;
    font-size: 12px;
    opacity: 0.7;
  }
  
  details[open] summary::after {
    transform: translateY(-50%) rotate(180deg);
  }
  
  /* Контент внутри details */
  details > div {
    padding: 0;
    margin: 0;
    overflow: visible;
    /* Убираем все ограничения */
    max-height: none;
    height: auto;
  }
  
  details > div > a {
    display: block;
    padding: 10px 24px;
    margin: 0;
    border-radius: 0;
    transition: background-color 0.2s ease;
    text-decoration: none;
    color: inherit;
    border-bottom: 1px solid rgba(0,0,0,.05);
  }
  
  details > div > a:last-child {
    border-bottom: none;
  }
  
  details > div > a:hover {
    background: rgba(0,0,0,.04);
  }

  .row{ display:flex; gap:8px; align-items:center; flex-wrap:wrap; padding:8px; }
  .stack{ display:grid; gap:10px; padding:12px; }
`;

const Footer = styled.div`
  margin-top:auto; padding:12px; border-top:1px solid var(--bdr); display:grid; gap:8px;
  a.compact{ padding:10px 12px; border-radius:12px; background:var(--card); border:1px solid var(--bdr); }
  .row{ display:flex; gap:8px; align-items:center; }
`;

const IconBtn = styled.a`
  width:40px; height:40px; border-radius:12px;
  display:flex; align-items:center; justify-content:center;
  border:1px solid transparent; flex:0 0 auto;
`;
const WaBtn = styled(IconBtn)`
  background: var(--card);
  border-color: var(--bdr);
  color: var(--fg);
  box-shadow: var(--shadow);
  svg{ width:20px; height:20px; display:block; }
`;
const TgBtn = styled(IconBtn)`
  background: var(--card);
  border-color: var(--bdr);
  color: var(--fg);
  box-shadow: var(--shadow);
  svg{ width:20px; height:20px; display:block; }
`;
const ZenBtn = styled(IconBtn)`
  background: var(--card);
  border-color: var(--bdr);
  color: var(--fg);
  box-shadow: var(--shadow);
  svg{ width:20px; height:20px; display:block; }
`;
const VkBtn = styled(IconBtn)`
  background: var(--card);
  border-color: var(--bdr);
  color: var(--fg);
  box-shadow: var(--shadow);
  svg{ width:20px; height:20px; display:block; }
`;
const OkBtn = styled(IconBtn)`
  background: var(--card);
  border-color: var(--bdr);
  color: var(--fg);
  box-shadow: var(--shadow);
  svg{ width:20px; height:20px; display:block; }
`;

const PrimaryBtn = styled.button`
  width:100%;
  padding:12px 14px;
  border-radius:14px;
  border: none;
  background: var(--primary);
  color: var(--primaryFg);
  font-weight:700;
  font-size:15px;
  cursor:pointer;
  box-shadow: var(--shadow);
  transition: transform .15s ease;
  &:active{ transform: scale(.98); }
`;

/* ====== Icons ====== */
function WaIcon(){
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 11.5a9 9 0 1 1-4.3-7.7" />
      <path d="M7.5 8.5c.3 2 2.5 4.2 4.5 4.8M12 13.3c.7-.5 1.2-1 1.6-1.6" />
      <path d="M3.5 20.5l2.5-.7" />
    </svg>
  );
}

function TgIcon(){
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 2L11 13" />
      <path d="M22 2l-7 20-4-9-9-4 20-7z" />
    </svg>
  );
}

function ZenIcon(){
  return (
    <svg viewBox="0 0 28 28" aria-hidden="true">
      <path d="M16.7 16.7c-2.2 2.27-2.36 5.1-2.55 11.3 5.78 0 9.77-.02 11.83-2.02 2-2.06 2.02-6.24 2.02-11.83-6.2.2-9.03.35-11.3 2.55M0 14.15c0 5.59.02 9.77 2.02 11.83 2.06 2 6.05 2.02 11.83 2.02-.2-6.2-.35-9.03-2.55-11.3-2.27-2.2-5.1-2.36-11.3-2.55M13.85 0C8.08 0 4.08.02 2.02 2.02.02 4.08 0 8.26 0 13.85c6.2-.2 9.03-.35 11.3-2.55 2.2-2.27 2.36-5.1 2.55-11.3m2.85 11.3C14.5 9.03 14.34 6.2 14.15 0c5.78 0 9.77.02 11.83 2.02 2 2.06 2.02 6.24 2.02 11.83-6.2-.2-9.03-.35-11.3-2.55" fill="#2C3036"/>
      <path fill="currentColor" d="M28 14.15v-.3c-6.2-.2-9.03-.35-11.3-2.55-2.2-2.27-2.36-5.1-2.55-11.3h-.3c-.2 6.2-.35 9.03-2.55 11.3-2.27 2.2-5.1 2.36-11.3 2.55v.3c6.2.2 9.03.35 11.3 2.55 2.2 2.27 2.36 5.1 2.55 11.3h.3c.2-6.2.35-9.03 2.55-11.3 2.27-2.2 5.1-2.36 11.3-2.55" style={{fill: 'transparent'}}/>
    </svg>
  );
}

function VkIcon(){
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
      <path d="M15.684 0H8.316C1.592 0 0 1.592 0 8.316v7.368C0 22.408 1.592 24 8.316 24h7.368C22.408 24 24 22.408 24 15.684V8.316C24 1.592 22.408 0 15.684 0zm3.692 17.123h-1.744c-.66 0-.864-.525-2.05-1.727-1.033-1.01-1.49-.9-1.744-.9-.356 0-.458.102-.458.593v1.575c0 .424-.135.678-1.253.678-1.846 0-3.896-1.118-5.335-3.202C4.624 10.857 4.03 8.57 4.03 8.096c0-.254.102-.491.593-.491h1.744c.441 0 .61.203.78.678.863 2.49 2.303 4.675 2.896 4.675.22 0 .322-.102.322-.66V9.721c-.068-1.186-.695-1.287-.695-1.71 0-.204.169-.407.441-.407h2.744c.373 0 .508.203.508.643v3.473c0 .372.169.508.271.508.22 0 .407-.136.813-.542 1.254-1.406 2.151-3.574 2.151-3.574.119-.254.322-.491.763-.491h1.744c.525 0 .644.271.525.643-.22 1.017-2.354 4.031-2.354 4.031-.186.305-.254.44 0 .78.186.254.796.779 1.203 1.253.745.847 1.32 1.558 1.473 2.05.17.49-.085.744-.576.744z"/>
    </svg>
  );
}

function OkIcon(){
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
      <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm0 4.8c1.988 0 3.6 1.612 3.6 3.6S13.988 12 12 12s-3.6-1.612-3.6-3.6S10.012 4.8 12 4.8zm0 2.4c-.663 0-1.2.537-1.2 1.2s.537 1.2 1.2 1.2 1.2-.537 1.2-1.2-.537-1.2-1.2-1.2zm5.4 7.2c0 .663-.537 1.2-1.2 1.2h-1.8l2.4 2.4c.469.469.469 1.231 0 1.7-.234.234-.541.35-.85.35s-.616-.116-.85-.35L12 16.6l-3.1 3.1c-.234.234-.541.35-.85.35s-.616-.116-.85-.35c-.469-.469-.469-1.231 0-1.7l2.4-2.4H7.8c-.663 0-1.2-.537-1.2-1.2s.537-1.2 1.2-1.2h8.4c.663 0 1.2.537 1.2 1.2z"/>
    </svg>
  );
}

export default function MobileDrawer({ open, onClose, onOpenQuiz }){
  const { t, i18n } = useTranslation();
  const favCount = useAppStore(s => s.favorites.length);
  const themeName = useAppStore(s => s.theme);
  const setTheme = useAppStore(s => s.setTheme);
  const lang = useAppStore(s => s.lang);
  const setLang = useAppStore(s => s.setLang);

  // Загрузка услуг из API
  const { services } = useServices();

  // Определяем текущий язык для логотипов
  const currentLang = i18n.language || lang || 'ru';
  
  // Функция для получения правильного логотипа
  const getMobileLogoPath = () => {
    const logoMap = {
      ru: "/ASTORIUS_FAVICON_RU.svg",
      en: "/ASTORIUS_FAVICON_ENG.svg",
      zh: "/ASTORIUS_FAVICON_CHINA.svg"
    };
    
    return logoMap[currentLang] || logoMap['ru'];
  };

  const toggleTheme = (e) => { e.stopPropagation(); setTheme(themeName === 'dark' ? 'light' : 'dark'); };
  const onLang = (lng) => { i18n.changeLanguage(lng); setLang(lng); };

  // блокируем скролл body + Esc для закрытия
  React.useEffect(() => {
    if (typeof document === 'undefined') return;
    const body = document.body;
    const prev = body.style.overflow;
    if (open) body.style.overflow = 'hidden';
    const onKey = (e) => { if (open && e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => { body.style.overflow = prev; window.removeEventListener('keydown', onKey); };
  }, [open, onClose]);

  // закрываем ТОЛЬКО при клике по ссылке
  const closeOnLink = () => { onClose(); };
  const openQuiz = () => {
    onClose();
    onOpenQuiz?.();
  };

  return (
    <>
      <Overlay open={open} onClick={onClose} />
      <Drawer open={open} role="dialog" aria-modal="true" aria-label={t('nav.realty')}>
        <Head>
          <Link to="/" className="logo" onClick={onClose}>
            <img 
              src={getMobileLogoPath()} 
              alt="ASTORIUS" 
            />
          </Link>
          <button onClick={onClose} aria-label="Close">✕</button>
        </Head>

        <Body>
          <div className="group">
            <div className="stack" onClick={(e)=>e.stopPropagation()}>
              <RegionSelect includeAllOption={false} />
              <PrimaryBtn type="button" onClick={openQuiz}>
                {t('pickObject')}
              </PrimaryBtn>
              <PrimaryBtn type="button" onClick={openQuiz} style={{background: 'var(--card)', color: 'var(--fg)', border: '1px solid var(--bdr)'}}>
                {t('home.quickTitle')}
              </PrimaryBtn>
            </div>
          </div>

          <div className="group">
            <details>
              <summary>{t('nav.realty')}</summary>
              <div>
                <Link to="/search" onClick={closeOnLink}>{t('nav.newbuilds')}</Link>
                <Link to="/secondary" onClick={closeOnLink}>{t('nav.secondary')}</Link>
                <Link to="/commercial" onClick={closeOnLink}>{t('nav.commercial')}</Link>
                <Link to="/foreign" onClick={closeOnLink}>{t('nav.foreign')}</Link>
                <Link to="/suburban" onClick={closeOnLink}>{t('nav.suburban')}</Link>
                <Link to="/mansions" onClick={closeOnLink}>{t('nav.mansions')}</Link>
                <Link to="/parking" onClick={closeOnLink}>{t('nav.parking')}</Link>
                <Link to="/rent" onClick={closeOnLink}>{t('nav.rent')}</Link>
              </div>
            </details>
          </div>

          <div className="group">
            <details>
              <summary>{t('nav.corporation')}</summary>
              <div>
                <Link to="/about" onClick={closeOnLink}>{t('header.servicesMenu.about')}</Link>
                <Link to="/mission" onClick={closeOnLink}>{t('mobileMenu.mission')}</Link>
                <Link to="/values" onClick={closeOnLink}>{t('mobileMenu.values')}</Link>
                <Link to="/directions" onClick={closeOnLink}>{t('mobileMenu.directions')}</Link>
                <Link to="/culture" onClick={closeOnLink}>{t('mobileMenu.culture')}</Link>
                <Link to="/contacts" onClick={closeOnLink}>{t('contacts.title')}</Link>
              </div>
            </details>
          </div>

          <div className="group">
            <details>
              <summary>{t('services')}</summary>
              <div>
                {services.map(service => (
                  <Link 
                    key={service.id} 
                    to={service.link || `/services/${service.id}`} 
                    onClick={closeOnLink}
                  >
                    {service.title}
                  </Link>
                ))}
              </div>
            </details>
          </div>

          <div className="group">
            <details>
              <summary>{t('collections.title')}</summary>
              <div>
                <Link to="/districts" onClick={closeOnLink}>{t('districts')}</Link>
                <Link to="/collections/weekly" onClick={closeOnLink}>{t('mobileMenu.weeklyRecommendations')}</Link>
                <Link to="/collections/installments" onClick={closeOnLink}>{t('mobileMenu.bestInstallments')}</Link>
                <Link to="/collections/cao" onClick={closeOnLink}>{t('mobileMenu.caoHouses')}</Link>
                <Link to="/collections/waterfront" onClick={closeOnLink}>{t('mobileMenu.waterfrontHouses')}</Link>
              </div>
            </details>
          </div>

          <div className="group">
            <Link to="/sell" onClick={closeOnLink}>{t('footer.sellProperty')}</Link>
            <Link to="/catalogs" onClick={closeOnLink}>{t('mobileMenu.authorCatalogs')}</Link>
            <Link to="/map" onClick={closeOnLink}>{t('footer.map')}</Link>
            <Link to="/contacts" onClick={closeOnLink}>{t('contacts.title')}</Link>
            <Link to="/socialmedia" onClick={closeOnLink}>{t('media')}</Link>
            <Link to="/blog" onClick={closeOnLink}>{t('blog')}</Link>
            <Link to="/vacancies" onClick={closeOnLink}>{t('footer.vacancies')}</Link>
            <Link to="/reviews" onClick={closeOnLink}>{t('mobileMenu.reviews')}</Link>
            <Link to="/awards" onClick={closeOnLink}>{t('mobileMenu.awards')}</Link>
            <Link to="/promotions" onClick={closeOnLink}>{t('mobileMenu.promotions')}</Link>
            <Link to="/partners" onClick={closeOnLink}>{t('mobileMenu.partners')}</Link>
            <Link to="/privacy" onClick={closeOnLink}>{t('footer.privacy')}</Link>
            <Link to="/admin" onClick={closeOnLink}>{t('mobileMenu.admin')}</Link>
          </div>

          <div className="group">
            <div className="row" onClick={(e)=>e.stopPropagation()} style={{alignItems:'center', padding:'12px'}}>
              <div className="currency-switcher"><CurrencySwitcher /></div>
              <div className="lang-switcher"><LangSwitcher lang={lang} onChange={onLang} /></div>
              <button
                onClick={toggleTheme}
                className="compact theme-toggle"
                style={{border:'1px solid var(--bdr)', borderRadius:12, padding:0, background:'var(--card)'}}
                aria-label={t('mobileMenu.theme')}
              >
                {themeName === 'dark' ? (
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{display:'block'}}>
                    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
                  </svg>
                ) : (
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{display:'block'}}>
                    <circle cx="12" cy="12" r="4"></circle>
                    <line x1="12" y1="1" x2="12" y2="3"></line>
                    <line x1="12" y1="21" x2="12" y2="23"></line>
                    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
                    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
                    <line x1="1" y1="12" x2="3" y2="12"></line>
                    <line x1="21" y1="12" x2="23" y2="12"></line>
                    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
                    <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
                  </svg>
                )}
              </button>
              <Link to="/favorites" onClick={closeOnLink} className="compact" style={{border:'1px solid var(--bdr)', borderRadius:12, padding:'8px 10px', background:'var(--card)'}}>
                {t('favorites')}{!!favCount && <span style={{marginLeft:6, fontWeight:800}}>({favCount})</span>}
              </Link>
            </div>
          </div>
        </Body>

        <Footer>
          <a href={`tel:${PHONE_TEL}`} className="compact"><strong>{PHONE_DISPLAY}</strong></a>
          <div className="row" onClick={(e)=>e.stopPropagation()}>
            <TgBtn href={TELEGRAM_URL} target="_blank" rel="noreferrer" title="Telegram" aria-label="Telegram">
              <TgIcon />
            </TgBtn>
            <ZenBtn href={ZEN_URL} target="_blank" rel="noreferrer" title="Яндекс Дзен" aria-label="Яндекс Дзен">
              <ZenIcon />
            </ZenBtn>
            <VkBtn href={VK_URL} target="_blank" rel="noreferrer" title="ВКонтакте" aria-label="ВКонтакте">
              <VkIcon />
            </VkBtn>
            <OkBtn href={OK_URL} target="_blank" rel="noreferrer" title="Одноклассники" aria-label="Одноклассники">
              <OkIcon />
            </OkBtn>
          </div>
        </Footer>
      </Drawer>
    </>
  );
}
